import React, { useState } from 'react';
import { Plus, FileText, Check, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { paymentService } from '@/lib/api';

const PaymentHistoryDialog = ({ open, onOpenChange, debtor, onUpdate }) => {
  const [payments, setPayments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: '', method: 'transfer', reference: '', notes: '' });
  const [newPlan, setNewPlan] = useState({ total_amount: '', installments: '3', interest_rate: '0' });

  React.useEffect(() => {
    if (debtor && open) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debtor, open]);

  const loadHistory = async () => {
    try {
      const [paymentsRes, plansRes] = await Promise.all([
        paymentService.getDebtorPayments(debtor.id),
        paymentService.getPlans(debtor.id)
      ]);
      setPayments(paymentsRes.data);
      setPlans(plansRes.data || []);
    } catch { toast.error('Error al cargar historial'); }
  };

  const handleRecordPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) { toast.error('Ingresa un monto valido'); return; }
    try {
      await paymentService.recordPayment(debtor.id, parseFloat(newPayment.amount), newPayment.method, newPayment.reference, newPayment.notes);
      toast.success('Pago registrado');
      setShowRecordPayment(false);
      setNewPayment({ amount: '', method: 'transfer', reference: '', notes: '' });
      loadHistory();
      onUpdate?.();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar pago'); }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.total_amount || parseFloat(newPlan.total_amount) <= 0) { toast.error('Ingresa un monto valido'); return; }
    if (!newPlan.installments || parseInt(newPlan.installments) < 2) { toast.error('Ingresa al menos 2 cuotas'); return; }
    try {
      await paymentService.createPlan(debtor.id, {
        total_amount: parseFloat(newPlan.total_amount),
        installments: parseInt(newPlan.installments),
        interest_rate: parseFloat(newPlan.interest_rate) || 0
      });
      toast.success('Plan de pago creado');
      setShowCreatePlan(false);
      setNewPlan({ total_amount: '', installments: '3', interest_rate: '0' });
      loadHistory();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al crear plan'); }
  };

  const handleRecordPlanPayment = async (planId) => {
    try {
      await paymentService.recordPlanPayment(planId);
      toast.success('Cuota registrada');
      loadHistory();
      onUpdate?.();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar cuota'); }
  };

  if (!debtor) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-purple-600" />
              Historial de Pagos - {debtor.name}
            </DialogTitle>
            <DialogDescription>Deuda actual: ${(debtor.total_debt || 0).toFixed(2)}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex gap-2">
              <Button onClick={() => setShowRecordPayment(true)} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="w-4 h-4 mr-2" />Registrar Pago
              </Button>
              <Button onClick={() => { setNewPlan({ ...newPlan, total_amount: debtor.total_debt?.toString() || '' }); setShowCreatePlan(true); }} variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50">
                <FileText className="w-4 h-4 mr-2" />Crear Plan de Cuotas
              </Button>
            </div>

            {/* Payment Plans */}
            {plans.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">Planes de Pago</h4>
                {plans.map((plan) => (
                  <div key={plan.id} className={`border rounded-lg p-4 ${plan.status === 'completed' ? 'bg-emerald-50 border-emerald-200' : 'bg-violet-50 border-violet-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-slate-900">${plan.total_with_interest.toFixed(2)} en {plan.installments} cuotas</p>
                        <p className="text-sm text-slate-600">${plan.installment_amount.toFixed(2)} /cuota{plan.interest_rate > 0 && ` (${plan.interest_rate}% interes)`}</p>
                      </div>
                      <Badge className={plan.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : plan.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-violet-100 text-violet-700'}>
                        {plan.status === 'completed' ? 'Completado' : plan.status === 'cancelled' ? 'Cancelado' : 'Activo'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 bg-slate-200 rounded-full h-2">
                        <div className="bg-violet-600 h-2 rounded-full transition-all" style={{ width: `${(plan.paid_installments / plan.installments) * 100}%` }} />
                      </div>
                      <span className="text-sm text-slate-600">{plan.paid_installments}/{plan.installments}</span>
                    </div>
                    {plan.status === 'active' && plan.paid_installments < plan.installments && (
                      <Button size="sm" onClick={() => handleRecordPlanPayment(plan.id)} className="bg-violet-600 hover:bg-violet-700">
                        <Check className="w-4 h-4 mr-1" />Registrar Cuota {plan.paid_installments + 1}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Payment History */}
            <div>
              <h4 className="font-medium text-slate-900 mb-3">Pagos Registrados</h4>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">No hay pagos registrados</div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment) => (
                    <div key={payment.id} className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-slate-900">${payment.amount.toFixed(2)}</p>
                          <p className="text-sm text-slate-500">{new Date(payment.payment_date).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                        <Badge variant="outline">
                          {payment.payment_method === 'transfer' ? 'Transferencia' : payment.payment_method === 'mercadopago' ? 'Mercado Pago' : payment.payment_method === 'cash' ? 'Efectivo' : payment.payment_method === 'plan_installment' ? 'Cuota Plan' : 'Otro'}
                        </Badge>
                      </div>
                      {payment.reference && <p className="text-sm text-slate-600 mt-2">Ref: {payment.reference}</p>}
                      {payment.notes && <p className="text-sm text-slate-500 mt-1">{payment.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={showRecordPayment} onOpenChange={setShowRecordPayment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
            <DialogDescription>Registrar un pago manual para {debtor.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Monto *</Label><Input type="number" value={newPayment.amount} onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })} placeholder="0.00" className="mt-1" /></div>
            <div>
              <Label>Metodo de Pago</Label>
              <Select value={newPayment.method} onValueChange={(v) => setNewPayment({ ...newPayment, method: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                  <SelectItem value="mercadopago">Mercado Pago</SelectItem>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Referencia (opcional)</Label><Input value={newPayment.reference} onChange={(e) => setNewPayment({ ...newPayment, reference: e.target.value })} placeholder="No de operacion, recibo, etc." className="mt-1" /></div>
            <div><Label>Notas (opcional)</Label><Textarea value={newPayment.notes} onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })} placeholder="Observaciones..." rows={2} className="mt-1" /></div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowRecordPayment(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleRecordPayment} className="flex-1 bg-teal-600 hover:bg-teal-700">Registrar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Payment Plan Modal */}
      <Dialog open={showCreatePlan} onOpenChange={setShowCreatePlan}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-violet-600" />Crear Plan de Pago</DialogTitle>
            <DialogDescription>Crear un plan de cuotas para {debtor.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Monto Total *</Label><Input type="number" value={newPlan.total_amount} onChange={(e) => setNewPlan({ ...newPlan, total_amount: e.target.value })} placeholder="0.00" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Numero de Cuotas *</Label>
                <Select value={newPlan.installments} onValueChange={(v) => setNewPlan({ ...newPlan, installments: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{[2, 3, 4, 5, 6, 9, 12, 18, 24].map(n => <SelectItem key={n} value={n.toString()}>{n} cuotas</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Interes por cuota (%)</Label><Input type="number" value={newPlan.interest_rate} onChange={(e) => setNewPlan({ ...newPlan, interest_rate: e.target.value })} placeholder="0" min="0" step="0.5" className="mt-1" /></div>
            </div>

            {newPlan.total_amount && newPlan.installments && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <p className="text-sm font-medium text-violet-800 mb-2">Vista previa del plan:</p>
                {(() => {
                  const total = parseFloat(newPlan.total_amount) || 0;
                  const cuotas = parseInt(newPlan.installments) || 1;
                  const interes = parseFloat(newPlan.interest_rate) || 0;
                  const totalInteres = total * (interes / 100) * cuotas;
                  const totalConInteres = total + totalInteres;
                  const cuotaMensual = totalConInteres / cuotas;
                  return (
                    <div className="text-sm text-violet-700 space-y-1">
                      <p>Monto base: ${total.toFixed(2)}</p>
                      {interes > 0 && <p>Interes total: ${totalInteres.toFixed(2)}</p>}
                      <p className="font-semibold">Total a pagar: ${totalConInteres.toFixed(2)}</p>
                      <p className="font-semibold">Cuota: ${cuotaMensual.toFixed(2)} x {cuotas}</p>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCreatePlan(false)} className="flex-1">Cancelar</Button>
              <Button onClick={handleCreatePlan} className="flex-1 bg-violet-600 hover:bg-violet-700">Crear Plan</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentHistoryDialog;
